import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import api, { formatError } from "../lib/api";
import { Coins, Clock, Users, ArrowRight, Briefcase, Code, Search as SearchIcon, BarChart3, Globe, Settings2, Zap, Filter, ChevronDown } from "lucide-react";

const CATEGORIES = [
  { value: "", label: "All Categories", icon: Filter },
  { value: "customer_growth", label: "Customer Growth", icon: BarChart3 },
  { value: "hiring", label: "Hiring", icon: Users },
  { value: "automation", label: "Automation", icon: Zap },
  { value: "local_business", label: "Local Business", icon: Globe },
  { value: "web_digital", label: "Web & Digital", icon: Code },
  { value: "operations", label: "Operations", icon: Settings2 },
];

export default function EarnPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [catOpen, setCatOpen] = useState(false);
  const [claimingId, setClaimingId] = useState(null);
  const [wallet, setWallet] = useState(null);

  useEffect(() => { fetchOrders(); if (user) fetchWallet(); }, [category, user]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = { status: "open" };
      if (category) params.category = category;
      const res = await api.get("/api/orders/tasks", { params });
      setOrders(res.data.orders || []);
    } catch (e) { console.error(formatError(e)); }
    finally { setLoading(false); }
  };

  const fetchWallet = async () => {
    try { const res = await api.get("/api/wallet/balance"); setWallet(res.data); } catch (_) {}
  };

  const handleClaim = async (orderId) => {
    if (!user) { navigate("/login"); return; }
    setClaimingId(orderId);
    try { await api.post(`/api/orders/tasks/${orderId}/claim`, { message: "" }); fetchOrders(); fetchWallet(); }
    catch (e) { alert(formatError(e)); }
    finally { setClaimingId(null); }
  };

  const selectedCat = CATEGORIES.find(c => c.value === category) || CATEGORIES[0];

  return (
    <div style={{ paddingTop: 60, minHeight: "100vh" }}>
      {/* Hero */}
      <section style={{ position: "relative", padding: "90px 24px 50px", overflow: "hidden" }}>
        <div className="bg-blob" style={{ width: 350, height: 350, background: "rgba(0,92,238,0.05)", top: "-5%", right: "0%", animationDelay: "0s" }} />
        <div className="bg-blob" style={{ width: 250, height: 250, background: "rgba(59,130,246,0.04)", bottom: "0%", left: "10%", animationDelay: "3s" }} />
        <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2 }}>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.7rem", fontWeight: 600, color: "var(--accent)", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "0.8rem" }}>Earn with TIE</p>
            <h1 data-testid="earn-page-title" style={{ fontFamily: "'Manrope', sans-serif", fontSize: "clamp(2rem, 5vw, 3rem)", fontWeight: 300, color: "var(--text-main)", lineHeight: 1.1, marginBottom: 12, letterSpacing: "-0.04em" }}>
              Execute tasks.<br /><span style={{ fontWeight: 700 }}>Earn credits.</span>
            </h1>
            <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.95rem", color: "var(--text-secondary)", maxWidth: 480, lineHeight: 1.6, marginBottom: 24 }}>
              Claim tasks posted by verified businesses. Complete the work, submit proof, and earn Execution Credits.
            </p>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Briefcase size={14} color="var(--text-muted)" strokeWidth={1.5} />
                <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                  <strong style={{ color: "var(--text-main)" }}>{orders.length}</strong> open tasks
                </span>
              </div>
              {wallet && (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Coins size={14} color="var(--accent)" strokeWidth={1.5} />
                    <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                      <strong style={{ color: "var(--text-main)" }}>{wallet.balance}</strong> credits
                    </span>
                  </div>
                  {wallet.pending_earnings > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Clock size={14} color="var(--text-muted)" strokeWidth={1.5} />
                      <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                        <strong style={{ color: "var(--text-main)" }}>{wallet.pending_earnings}</strong> pending
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px 28px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase" }}>Filter</span>
          <div style={{ position: "relative" }}>
            <button data-testid="earn-category-filter" onClick={() => setCatOpen(!catOpen)} style={{
              display: "flex", alignItems: "center", gap: 7, padding: "0.45rem 0.9rem", borderRadius: 10,
              background: "rgba(255,255,255,0.6)", backdropFilter: "blur(8px)", border: "1px solid var(--bg-neutral)",
              cursor: "pointer", fontFamily: "'Manrope', sans-serif", fontSize: "0.82rem", fontWeight: 500,
              color: "var(--text-main)", transition: "all 200ms",
            }}>
              {React.createElement(selectedCat.icon, { size: 13, color: "var(--accent)", strokeWidth: 1.5 })}
              {selectedCat.label}
              <ChevronDown size={11} style={{ color: "var(--text-muted)", transform: catOpen ? "rotate(180deg)" : "none", transition: "transform 200ms" }} />
            </button>
            {catOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: 210, zIndex: 50,
                background: "rgba(255,255,255,0.95)", backdropFilter: "blur(20px)",
                border: "1px solid rgba(255,255,255,0.5)", borderRadius: 12,
                boxShadow: "0 12px 40px rgba(0,0,0,0.08)", padding: "4px",
              }}>
                {CATEGORIES.map(cat => (
                  <button key={cat.value} data-testid={`earn-cat-${cat.value || "all"}`}
                    onClick={() => { setCategory(cat.value); setCatOpen(false); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 8, width: "100%",
                      padding: "0.5rem 0.8rem", borderRadius: 8,
                      background: category === cat.value ? "var(--accent-subtle)" : "transparent",
                      border: "none", cursor: "pointer", fontFamily: "'Manrope', sans-serif",
                      fontSize: "0.82rem", color: category === cat.value ? "var(--accent-text)" : "var(--text-main)",
                      fontWeight: category === cat.value ? 600 : 400, textAlign: "left", transition: "background 150ms",
                    }}
                    onMouseEnter={e => { if (category !== cat.value) e.currentTarget.style.background = "var(--bg-soft)"; }}
                    onMouseLeave={e => { if (category !== cat.value) e.currentTarget.style.background = "transparent"; }}>
                    {React.createElement(cat.icon, { size: 13, strokeWidth: 1.5 })} {cat.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Task Grid */}
      <section style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px 80px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}><div className="loading-spinner" /></div>
        ) : orders.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <SearchIcon size={36} color="var(--bg-neutral)" strokeWidth={1.5} style={{ marginBottom: 12 }} />
            <p style={{ fontFamily: "'Manrope', sans-serif", color: "var(--text-muted)" }}>No open tasks right now. Check back soon.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))", gap: 16 }}>
            {orders.map((order, i) => (
              <motion.div key={order.id} data-testid={`earn-task-card-${i}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, duration: 0.3 }} className="glass-card" style={{ padding: "1.6rem", display: "flex", flexDirection: "column", gap: 14 }}>
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.68rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.12em" }}>
                    {order.category?.replace(/_/g, " ")}
                  </p>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, background: "var(--accent-subtle)", padding: "3px 10px", borderRadius: 100, border: "1px solid rgba(59,130,246,0.1)" }}>
                    <Coins size={12} color="var(--accent)" strokeWidth={1.5} />
                    <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "0.88rem", color: "var(--accent-text)" }}>{order.credit_reward}</span>
                  </div>
                </div>
                {/* Title & Description */}
                <div>
                  <h3 style={{ fontFamily: "'Manrope', sans-serif", fontSize: "1rem", fontWeight: 600, color: "var(--text-main)", lineHeight: 1.3, marginBottom: 5 }}>{order.title}</h3>
                  <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.82rem", color: "var(--text-secondary)", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{order.description}</p>
                </div>
                {/* Skills */}
                {order.skills_required?.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {order.skills_required.map(skill => (
                      <span key={skill} style={{ padding: "2px 9px", borderRadius: 100, background: "var(--bg-neutral)", color: "var(--text-secondary)", fontFamily: "'Manrope', sans-serif", fontSize: "0.7rem", fontWeight: 500 }}>{skill}</span>
                    ))}
                  </div>
                )}
                {/* Footer */}
                <div style={{ marginTop: "auto", display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, borderTop: "1px solid rgba(233,238,245,0.6)" }}>
                  <div style={{ display: "flex", gap: 14 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 4, fontFamily: "'Manrope', sans-serif", fontSize: "0.72rem", color: "var(--text-muted)" }}>
                      <Clock size={11} strokeWidth={1.5} /> {order.deadline_hours}h
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 4, fontFamily: "'Manrope', sans-serif", fontSize: "0.72rem", color: "var(--text-muted)" }}>
                      <Users size={11} strokeWidth={1.5} /> {order.claims_count}/{order.max_claims}
                    </span>
                  </div>
                  <button data-testid={`claim-task-${i}`} onClick={() => handleClaim(order.id)} disabled={claimingId === order.id}
                    className="btn-primary" style={{ padding: "0.4rem 1rem", fontSize: "0.78rem", borderRadius: 8, opacity: claimingId === order.id ? 0.5 : 1, display: "flex", alignItems: "center", gap: 5 }}>
                    {claimingId === order.id ? "Claiming..." : "Claim"} <ArrowRight size={12} strokeWidth={1.5} />
                  </button>
                </div>
                <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.68rem", color: "var(--text-muted)" }}>
                  by <span style={{ fontWeight: 500 }}>{order.poster_name}</span>
                </p>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* CTA for non-logged-in */}
      {!user && (
        <section style={{ padding: "50px 24px 70px" }}>
          <div style={{ maxWidth: 500, margin: "0 auto", textAlign: "center" }}>
            <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: "1.4rem", fontWeight: 300, marginBottom: 10, letterSpacing: "-0.03em" }}>
              Ready to <span style={{ fontWeight: 700 }}>start earning</span>?
            </h2>
            <p style={{ fontFamily: "'Manrope', sans-serif", color: "var(--text-muted)", marginBottom: 20, fontSize: "0.88rem" }}>
              Create a free account to claim tasks and earn Execution Credits.
            </p>
            <button data-testid="earn-cta-register" onClick={() => navigate("/register")} className="btn-primary" style={{ padding: "0.65rem 2rem" }}>Get started</button>
          </div>
        </section>
      )}
    </div>
  );
}
